<section [formGroup]="formGroup()">
  @if (isUserPlacementTypesEnabled()) {
    <section>
      <h5
        class="text-neutral-900 font-semibold text-lg py-lg border-b border-neutral-200 my-2xl">
        {{ 'user_form_screen_work_info_section_title' | transloco }}
      </h5>

      <div class="mb-4xl">
        <label class="block font-medium text-sm text-neutral-900 mb-x-sm">
          {{ 'user_form_screen_placement_type_label' | transloco }}
          <span class="text-destructive-500 text-sm">*</span>
        </label>
        <lib-radio-button
          [id]="'placement_type'"
          [formGroup]="formGroup()"
          [inputFormControlName]="'placement_type'"
          [required]="true"
          [class]="'flex gap-4xl'"
          [options]="placementTypeOptions()" />
        <lib-validation-message
          [lang]="'current_lang' | transloco"
          control="placement_type" />
      </div>

      @switch (formGroup().get('placement_type')?.value) {
        @case (PLACEMENT_TYPE_VALUES.DEPARTMENT_AND_JOB_TITLE) {
          <div
            class="mb-4xl grid [grid-template-columns:repeat(auto-fill,minmax(20rem,1fr))] gap-lg">
            <div>
              <lib-paginated-select
                #departmentSelect
                [id]="'drop_down_text_field_department'"
                [inputFormControlName]="'department_id'"
                [name]="'department_id'"
                [required]="true"
                [label]="'user_form_screen_department_label' | transloco"
                [placeholder]="
                  'user_form_screen_department_placeholder' | transloco
                "
                [apiUrl]="departmentApi"
                [selectedOption]="selectedDepartment()"
                [hasSearch]="isQuickCreateEnabled()"
                [allowCreate]="isQuickCreateEnabled()"
                [createLabel]="
                  'user_form_screen_add_new_department_label' | transloco
                "
                (createNew)="openDepartmentQuickCreate($event)"
                [noDataLabel]="'user_dropdown_input_no_data_label' | transloco" />
              <lib-validation-message
                [lang]="'current_lang' | transloco"
                control="department_id" />
            </div>

            <div>
              <lib-paginated-select
                #jobTitleSelect
                [id]="'drop_down_text_field_job_title'"
                [inputFormControlName]="'job_title_id'"
                [name]="'job_title_id'"
                [required]="true"
                [label]="'user_form_screen_job_title_label' | transloco"
                [placeholder]="
                  'user_form_screen_job_title_placeholder' | transloco
                "
                [apiUrl]="jobTitleApi"
                [selectedOption]="selectedJobTitle()"
                [hasSearch]="isQuickCreateEnabled()"
                [allowCreate]="isQuickCreateEnabled()"
                [createLabel]="
                  'user_form_screen_add_new_job_title_label' | transloco
                "
                (createNew)="openJobTitleQuickCreate($event)"
                [noDataLabel]="'user_dropdown_input_no_data_label' | transloco" />
              <lib-validation-message
                [lang]="'current_lang' | transloco"
                control="job_title_id" />
            </div>

            <div>
              <lib-paginated-select
                [id]="'drop_down_text_field_direct_manager'"
                [inputFormControlName]="'direct_manager_id'"
                [name]="'direct_manager_id'"
                [label]="'user_form_screen_direct_manager_label' | transloco"
                [placeholder]="
                  'user_form_screen_direct_manager_placeholder' | transloco
                "
                [apiUrl]="directManagerApi()"
                [selectedOption]="selectedDirectManager()"
                [noDataLabel]="'user_dropdown_input_no_data_label' | transloco" />
              <lib-validation-message
                [lang]="'current_lang' | transloco"
                control="direct_manager_id" />
            </div>
          </div>
        }
        @case (PLACEMENT_TYPE_VALUES.VACANT_POSITION) {
          <div
            class="mb-4xl grid [grid-template-columns:repeat(auto-fill,minmax(20rem,1fr))] gap-lg">
            <div>
              <lib-lookup-field
                #positionLookup
                [id]="'position_id'"
                [inputFormControlName]="'position_id'"
                [name]="'position_id'"
                [label]="'user_form_screen_position_label' | transloco"
                [required]="false"
                [viewOnly]="false"
                [placeholder]="'user_form_screen_position_placeholder' | transloco"
                [apiUrl]="positionApi"
                [filters]="positionFilter"
                [selectedOption]="
                  selectedPosition() ? selectedPosition() : undefined
                "
                [selectedKeyName]="'name'"
                [selectedKeyValue]="'id'"
                [labelValueAssignList]="{
                  label: 'name',
                  value: 'id',
                  icon: 'image',
                  extraLabels: [
                    {
                      label:
                        'user_details_screen_legal_entity_label' | transloco,
                      key: 'legal_entity_name',
                    },
                    {
                      label: 'user_details_screen_department_label' | transloco,
                      key: 'department_name',
                    },
                    {
                      label: 'user_details_screen_job_title_label' | transloco,
                      key: 'job_title_name',
                    },
                    {
                      label:
                        'user_details_screen_direct_manager_label' | transloco,
                      key: 'direct_manager_name',
                    },
                  ],
                }"
                (selectedOptionOutput)="onSelectPosition($event)"
                [allowCreate]="isQuickCreateEnabled()"
                [createLabel]="
                  'user_form_screen_add_new_position_label' | transloco
                "
                (createNew)="openPositionQuickCreate($event)"
                [noSearchResultLabel]="
                  'user_dropdown_input_no_data_label' | transloco
                "
                [searchPlaceholder]="'type_to_search_placeholder' | transloco"
                [detailsNotFoundLabel]="'details_not_found_label' | transloco" />

              <lib-validation-message
                [lang]="'current_lang' | transloco"
                control="position_id" />
            </div>
          </div>
        }
        @case (PLACEMENT_TYPE_VALUES.EXTERNAL) {
          <p class="text-neutral-500 text-sm mb-4xl">
            {{ 'user_form_screen_placement_type_external_note' | transloco }}
          </p>
        }
      }

      @if (isQuickCreateEnabled()) {
        <ng-template #departmentQuickCreate>
          <lib-department-quick-create-form
            [legalEntity]="legalEntity()"
            [prefillName]="quickCreateSearchText()"
            (created)="onDepartmentCreated($event)"
            (cancelled)="closeQuickCreateModal()" />
        </ng-template>

        <ng-template #jobTitleQuickCreate>
          <lib-job-title-quick-create-form
            [prefillName]="quickCreateSearchText()"
            (created)="onJobTitleCreated($event)"
            (cancelled)="closeQuickCreateModal()" />
        </ng-template>

        <ng-template #positionQuickCreate>
          <lib-position-quick-create-form
            [legalEntity]="legalEntity()"
            [prefillName]="quickCreateSearchText()"
            (created)="onPositionCreated($event)"
            (cancelled)="closeQuickCreateModal()" />
        </ng-template>
      }
    </section>
  } @else if (isSelectThePositionWhenAddingANewUserEnabled()) {
    <section>
      <h5
        class="text-neutral-900 font-semibold text-lg py-lg border-b border-neutral-200 my-2xl">
        {{ 'user_form_screen_work_info_section_title' | transloco }}
      </h5>

      <div
        class="mb-4xl grid [grid-template-columns:repeat(auto-fill,minmax(20rem,1fr))] gap-lg">
        <div>
          <lib-lookup-field
            [id]="'position_id'"
            [inputFormControlName]="'position_id'"
            [name]="'position_id'"
            [label]="'user_form_screen_position_label' | transloco"
            [required]="false"
            [viewOnly]="false"
            [placeholder]="'user_form_screen_position_placeholder' | transloco"
            [apiUrl]="positionApi"
            [filters]="positionFilter"
            [selectedOption]="
              selectedPosition() ? selectedPosition() : undefined
            "
            [selectedKeyName]="'name'"
            [selectedKeyValue]="'id'"
            [labelValueAssignList]="{
              label: 'name',
              value: 'id',
              icon: 'image',
              extraLabels: [
                {
                  label: 'user_details_screen_legal_entity_label' | transloco,
                  key: 'legal_entity_name',
                },
                {
                  label: 'user_details_screen_department_label' | transloco,
                  key: 'department_name',
                },
                {
                  label: 'user_details_screen_job_title_label' | transloco,
                  key: 'job_title_name',
                },
                {
                  label:
                    'user_details_screen_direct_manager_label' | transloco,
                  key: 'direct_manager_name',
                },
              ],
            }"
            (selectedOptionOutput)="onSelectPosition($event)"
            [noSearchResultLabel]="
              'user_dropdown_input_no_data_label' | transloco
            "
            [searchPlaceholder]="'type_to_search_placeholder' | transloco"
            [detailsNotFoundLabel]="'details_not_found_label' | transloco" />

          <lib-validation-message
            [lang]="'current_lang' | transloco"
            control="position_id" />
        </div>
      </div>
    </section>
  } @else if (userId()) {
    <section>
      <h5
        class="text-neutral-900 font-semibold text-lg py-lg border-b border-neutral-200 my-2xl">
        {{ 'user_form_screen_work_info_section_title' | transloco }}
      </h5>

      <div
        class="mb-4xl grid [grid-template-columns:repeat(auto-fill,minmax(20rem,1fr))] gap-lg">
        <div>
          <p class="view-mode-label">
            {{ 'user_form_screen_position_label' | transloco }}
          </p>

          <p class="view-mode-value break-word text-neutral-900">
            {{
              userDetails()?.placement?.position?.name
                ?? ('details_not_found_label' | transloco)
            }}
          </p>
        </div>

        <div>
          <p class="view-mode-label">
            {{ 'user_details_screen_legal_entity_label' | transloco }}
          </p>

          <p class="view-mode-value break-word text-neutral-900">
            {{
              userDetails()?.placement?.position?.legal_entity?.name
                ?? ('details_not_found_label' | transloco)
            }}
          </p>
        </div>

        <div>
          <p class="view-mode-label">
            {{ 'user_details_screen_department_label' | transloco }}
          </p>

          <p class="view-mode-value break-word text-neutral-900">
            {{
              userDetails()?.placement?.department?.name
                ?? ('details_not_found_label' | transloco)
            }}
          </p>
        </div>

        <div>
          <p class="view-mode-label">
            {{ 'user_details_screen_job_title_label' | transloco }}
          </p>

          <p class="view-mode-value break-word text-neutral-900">
            {{
              userDetails()?.placement?.job_title?.name
                ?? ('details_not_found_label' | transloco)
            }}
          </p>
        </div>

        <div>
          <p class="view-mode-label">
            {{ 'user_details_screen_direct_manager_label' | transloco }}
          </p>

          <p class="view-mode-value break-word text-neutral-900">
            {{
              userDetails()?.placement?.direct_manager?.label
                ?? ('details_not_found_label' | transloco)
            }}
          </p>
        </div>
      </div>
    </section>
  }
</section>
